import { useState, useEffect, useCallback } from 'react';
import type { UserInfo } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
}

const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

export function useAuth(): AuthState {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      // In local development, mock an authenticated user for easier testing
      if (isDevelopment) {
        if (!cancelled) {
          setUser({
            clientPrincipal: {
              identityProvider: 'aad',
              userId: 'dev-user-id',
              userDetails: 'developer@localhost',
              userRoles: ['authenticated', 'anonymous'],
            },
          });
          setLoading(false);
        }
        return;
      }

      try {
        const response = await fetch('/.auth/me');
        if (!response.ok) {
          throw new Error(`Auth check failed: ${response.status}`);
        }
        const data: UserInfo = await response.json();
        if (!cancelled) {
          setUser(data);
        }
      } catch (error) {
        console.error('Failed to check authentication:', error);
        if (!cancelled) {
          setUser({ clientPrincipal: null });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(() => {
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=/';
  }, []);

  const logout = useCallback(() => {
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  }, []);

  const isAuthenticated = user?.clientPrincipal !== null && user?.clientPrincipal !== undefined;

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
  };
}
